import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Banknote, Clock, TrendingUp, ShoppingBag,
  ArrowUpRight, ExternalLink, Info, CreditCard, Check,
} from "lucide-react";

const money = (cents, currency = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase() }).format((cents || 0) / 100);

function BalanceCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-white/40">{label}</span>
        <Icon className={`h-4 w-4 ${accent || "text-[#00F0FF]"}`} />
      </div>
      <p className="font-display text-2xl font-bold mt-3">{value}</p>
    </div>
  );
}

function PayoutHistory({ payouts }) {
  if (!payouts || payouts.length === 0) {
    return (
      <div data-testid="no-payouts" className="rounded-2xl border border-dashed border-white/15 py-14 text-center text-white/40">
        <Banknote className="h-8 w-8 mx-auto mb-3" />
        No payouts yet. Your earnings will appear here once you withdraw.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-white/10 overflow-hidden">
      {payouts.map((p, i) => (
        <div key={p.id || i} data-testid={`payout-row-${i}`}
          className="flex items-center justify-between px-5 py-4 border-b border-white/5 last:border-0 bg-[#0d0d0d]">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#FFDD03]/10 text-[#FFDD03]">
              <ArrowUpRight className="h-4 w-4" />
            </span>
            <div>
              <p className="font-medium">{money(p.amount_cents, p.currency)}</p>
              <p className="text-xs text-white/40">
                {(p.arrival_date || p.created) ? new Date((p.arrival_date || p.created) * 1000).toLocaleDateString() : "—"}
              </p>
            </div>
          </div>
          <span className={`text-xs rounded-full px-3 py-1 capitalize ${
            p.status === "paid" || p.status === "success" ? "bg-emerald-500/15 text-emerald-400"
              : p.status === "failed" || p.status === "canceled" ? "bg-red-500/15 text-red-400"
              : "bg-white/10 text-white/60"
          }`}>
            {(p.status || "").replace(/_/g, " ")}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Payouts() {
  const { checkAuth } = useAuth();
  const [params, setParams] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/payouts/wallet");
      setWallet(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("connected")) {
      toast.success("Stripe account connected");
      checkAuth?.();
      setParams({}, { replace: true });
    }
  }, [params, setParams, checkAuth]);

  const connectStripe = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setConnecting(false);
    }
  };

  const withdrawStripe = async () => {
    setBusy("wd-stripe");
    try {
      const { data } = await api.post("/payouts/withdraw", {});
      toast.success(`Withdrawal of ${money(data.amount_cents, wallet.stripe.currency)} initiated`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(""); }
  };

  if (loading || !wallet) {
    return (
      <div className="min-h-screen bg-[#050505] grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  const lt = wallet.lifetime || { gross_cents: 0, fee_cents: 0, net_cents: 0, sales_count: 0 };
  const s = wallet.stripe;
  const stripeReady = s.connected && s.onboarded;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        <div className="mb-8">
          <h1 className="font-display text-3xl md:text-4xl font-bold">Payouts &amp; Wallet</h1>
          <p className="text-white/50 mt-1">Your clip-sale earnings, minus a 5% platform fee. Paid out automatically via Stripe.</p>
        </div>

        {/* Lifetime summary */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <BalanceCard testid="balance-lifetime" icon={TrendingUp} label="Lifetime earnings (net)" value={money(lt.net_cents)} accent="text-emerald-400" />
          <BalanceCard testid="balance-gross" icon={Banknote} label="Gross sales" value={money(lt.gross_cents)} />
          <BalanceCard testid="balance-sales" icon={ShoppingBag} label="Clips sold" value={lt.sales_count} />
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 mb-10 flex items-start gap-3 text-sm text-white/60">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-[#00F0FF]" />
          <p>Platform fee is <span className="text-white">5%</span> of each sale ({money(lt.fee_cents)} so far). Your 95% share lands in your Stripe balance automatically at checkout.</p>
        </div>

        {/* Stripe connect / balance */}
        {!stripeReady ? (
          <section data-testid="stripe-connect" className="rounded-2xl border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-8 text-center max-w-2xl">
            <span className="grid place-items-center h-14 w-14 rounded-2xl bg-[#00F0FF]/10 text-[#00F0FF] mx-auto mb-5">
              <CreditCard className="h-7 w-7" />
            </span>
            <h2 className="font-display text-2xl font-bold mb-2">Connect Stripe to get paid</h2>
            <p className="text-white/60 leading-relaxed mb-6">
              Money auto-splits at checkout — 95% lands in your Stripe balance instantly, and Stripe pays out to your bank on a schedule. It takes about 2 minutes to set up.
            </p>
            <button
              onClick={connectStripe}
              disabled={connecting}
              data-testid="connect-stripe-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-8 py-3 text-sm hover:scale-[1.03] transition-transform duration-300 disabled:opacity-60"
            >
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
              {s.connected ? "Finish Stripe setup" : "Connect with Stripe"}
            </button>
            {s.connected && (
              <p className="mt-3 text-xs text-[#FFDD03]">Your Stripe setup is incomplete — finish onboarding to start receiving payouts.</p>
            )}
          </section>
        ) : (
          <section data-testid="stripe-wallet">
            <div className="flex items-center justify-between mb-4 gap-4">
              <div className="flex items-center gap-2">
                <h2 className="font-display text-xl font-semibold">Stripe balance</h2>
                <span data-testid="stripe-active-badge" className="inline-flex items-center gap-1 text-xs rounded-full bg-emerald-500/15 text-emerald-400 px-3 py-1 font-semibold">
                  <Check className="h-3 w-3" /> Connected
                </span>
              </div>
              <button
                onClick={withdrawStripe}
                disabled={(s.available_cents || 0) <= 0 || busy === "wd-stripe" || !s.payouts_enabled}
                data-testid="withdraw-stripe-btn"
                className="inline-flex items-center gap-2 bg-[#FFDD03] text-black font-semibold rounded-full px-6 py-2.5 text-sm hover:scale-105 transition-transform duration-300 disabled:opacity-40 disabled:hover:scale-100"
              >
                {busy === "wd-stripe" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                Withdraw
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-8">
              <BalanceCard testid="stripe-available" icon={Banknote} label="Available" value={money(s.available_cents, s.currency)} accent="text-[#FFDD03]" />
              <BalanceCard testid="stripe-pending" icon={Clock} label="Pending" value={money(s.pending_cents, s.currency)} />
            </div>
            <h3 className="font-display text-lg font-semibold mb-4">Payout history</h3>
            <PayoutHistory payouts={s.payouts} />
          </section>
        )}
      </main>
    </div>
  );
}
